import { useCallback, useEffect, useState } from "react";
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { selectAccountData } from "../redux/main/accountSlice";

export const EquipmentSelect = ({ selectedOptions, setSelectedOptions, isMulti = true, placeholder = "Seleziona attrezzature...", menuPortalTarget, menuPosition, menuPlacement }) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');
    const accountData = useSelector(selectAccountData);
    const accountId = accountData?._id;

    // Funzione per eseguire la chiamata API
    const fetchEquipments = async (searchText) => {
        if (!accountId) return;
        
        setIsLoading(true);
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/equipments/list?accountId=${accountId}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                throw new Error(`Errore API: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Estrai l'array di attrezzature dal campo 'data' della risposta
            const items = data?.data || [];
            
            // Filtra per searchText se presente
            let filteredItems = items;
            if (searchText && searchText.trim() !== '') {
                const searchLower = searchText.toLowerCase();
                filteredItems = items.filter(eq => 
                    (eq.title && eq.title.toLowerCase().includes(searchLower)) ||
                    (eq.code && eq.code.toLowerCase().includes(searchLower))
                );
            }
            
            // Trasforma i dati nel formato richiesto da react-select
            const formattedOptions = filteredItems.map(equipment => ({
                value: equipment.id || equipment._id,
                label: equipment.title || 'Attrezzatura senza nome',
                title: equipment.title,
                data: equipment
            }));
            
            setOptions(formattedOptions);
        } catch (error) {
            console.error('Errore nel caricamento delle attrezzature:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Debounce per evitare troppe chiamate API durante la digitazione
    const debouncedFetchEquipments = useCallback(
        debounce(fetchEquipments, 300),
        [accountId, token]
    );

    // Carica le opzioni iniziali
    useEffect(() => {
        fetchEquipments('');
    }, [accountId, token]);

    // Gestisce la modifica dell'input di ricerca
    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        debouncedFetchEquipments(newValue);
    };

    // Stile personalizzato per react-select
    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#3182ce' : '#cbd5e0'
            },
            borderRadius: '0.375rem',
            backgroundColor: state.isDisabled ? '#f7fafc' : 'white',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#3182ce' 
                : state.isFocused 
                    ? '#ebf8ff' 
                    : base.backgroundColor,
            color: state.isSelected ? 'white' : '#4a5568',
            ':active': {
                backgroundColor: state.isSelected ? '#3182ce' : '#e6f7ff'
            },
            padding: '8px 12px'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#fef3c7',
            borderRadius: '0.25rem',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#92400e',
            fontWeight: 500,
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#92400e',
            ':hover': {
                backgroundColor: '#fcd34d',
                color: '#78350f'
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.375rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 10000000,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 10000000,
        }),
        noOptionsMessage: (base) => ({
            ...base,
            fontSize: '0.875rem',
            color: '#718096'
        })
    };

    return (
        <Select
            isMulti={isMulti}
            options={options}
            value={selectedOptions}
            onChange={setSelectedOptions}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            isLoading={isLoading}
            placeholder={placeholder}
            noOptionsMessage={() => "Nessuna attrezzatura trovata"}
            loadingMessage={() => "Caricamento attrezzature..."}
            styles={customStyles}
            isClearable
            closeMenuOnSelect={!isMulti}
            classNamePrefix="equipment-select"
            menuPortalTarget={menuPortalTarget !== undefined ? menuPortalTarget : document.body}
            menuPosition={menuPosition || "fixed"}
            menuPlacement={menuPlacement || "auto"}
        />
    );
};

export default EquipmentSelect;
